
import { useState, useEffect } from 'react';

interface Challenge {
  id: string;
  title: string;
  description: string;
  icon: string;
  progress: number;
  target: number;
  reward: number;
  rewardType: 'points' | 'xp';
  completed: boolean;
}

export const DailyChallenges = () => {
  const [challenges, setChallenges] = useState<Challenge[]>([]);

  useEffect(() => {
    // Gerar desafios diários
    const today = new Date().toDateString();
    const savedChallenges = localStorage.getItem('dailyChallenges');
    const savedDate = localStorage.getItem('dailyChallengesDate');

    if (savedChallenges && savedDate === today) {
      setChallenges(JSON.parse(savedChallenges));
    } else {
      // Gerar novos desafios
      const newChallenges: Challenge[] = [
        {
          id: '1',
          title: 'Completar 3 Missões',
          description: 'Complete 3 missões hoje',
          icon: 'ri-task-line',
          progress: 0,
          target: 3,
          reward: 100,
          rewardType: 'xp',
          completed: false
        },
        {
          id: '2',
          title: 'Ganhar 500 Pontos',
          description: 'Acumule 500 pontos hoje',
          icon: 'ri-coin-line',
          progress: 0,
          target: 500,
          reward: 50,
          rewardType: 'points',
          completed: false
        },
        {
          id: '3',
          title: 'Jogar 2 Jogos',
          description: 'Participe de 2 jogos diferentes',
          icon: 'ri-gamepad-line',
          progress: 0,
          target: 2,
          reward: 75,
          rewardType: 'xp',
          completed: false
        },
        {
          id: '4',
          title: 'Resgatar uma Recompensa',
          description: 'Resgate qualquer recompensa',
          icon: 'ri-gift-line',
          progress: 0,
          target: 1,
          reward: 100,
          rewardType: 'points',
          completed: false
        }
      ];
      
      setChallenges(newChallenges);
      localStorage.setItem('dailyChallenges', JSON.stringify(newChallenges));
      localStorage.setItem('dailyChallengesDate', today);
    }
  }, []);

  const completedCount = challenges.filter(c => c.completed).length;
  const totalCount = challenges.length;

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
            <i className="ri-trophy-line text-2xl text-white"></i>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">Desafios Diários</h3>
            <p className="text-sm text-gray-500">Renovam a cada 24 horas</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-purple-600">{completedCount}/{totalCount}</div>
          <div className="text-sm text-gray-500">Completos</div>
        </div>
      </div>

      <div className="space-y-4">
        {challenges.map((challenge) => (
          <div
            key={challenge.id}
            className={`border-2 rounded-lg p-4 transition-all ${
              challenge.completed
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-purple-300'
            }`}
          >
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                challenge.completed ? 'bg-green-500' : 'bg-gray-100'
              }`}>
                <i className={`${challenge.icon} text-2xl ${
                  challenge.completed ? 'text-white' : 'text-gray-600'
                }`}></i>
              </div>
              
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-semibold text-gray-800">{challenge.title}</h4>
                    <p className="text-sm text-gray-600">{challenge.description}</p>
                  </div>
                  <div className="flex items-center gap-1 text-sm font-semibold">
                    <span className={challenge.rewardType === 'points' ? 'text-yellow-600' : 'text-purple-600'}>
                      +{challenge.reward}
                    </span>
                    <i className={`${challenge.rewardType === 'points' ? 'ri-coin-line text-yellow-600' : 'ri-star-line text-purple-600'}`}></i>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Progresso</span>
                    <span className="font-semibold text-gray-800">
                      {challenge.progress}/{challenge.target}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        challenge.completed ? 'bg-green-500' : 'bg-gradient-to-r from-blue-500 to-purple-500'
                      }`}
                      style={{ width: `${(challenge.progress / challenge.target) * 100}%` }}
                    ></div>
                  </div>
                </div>
                
                {challenge.completed && (
                  <div className="mt-3 flex items-center gap-2 text-green-600 text-sm font-semibold">
                    <i className="ri-checkbox-circle-fill"></i>
                    <span>Desafio Completo!</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {completedCount === totalCount && (
        <div className="mt-6 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg p-4 text-center text-white">
          <i className="ri-trophy-fill text-4xl mb-2"></i>
          <div className="font-bold text-lg">🎉 Todos os desafios completos! 🎉</div>
          <div className="text-sm opacity-90 mt-1">Volte amanhã para novos desafios!</div>
        </div>
      )}
    </div>
  );
};
